/**
 * Utility functions for formatting various data types
 */

/**
 * Formats a currency value with proper locale formatting
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    return `${currency} ${amount.toFixed(2)}`;
  }
};

/**
 * Formats a relative time from a date (e.g., "2 hours ago", "in 5 minutes")
 */
export const formatRelativeTime = (
  date: Date,
  locale: string = 'en-US'
): string => {
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { 
      numeric: 'auto',
      style: 'long' 
    });

    const now = new Date();
    const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

    // Define time units in seconds
    const timeUnits: [string, number][] = [
      ['year', 31536000],
      ['month', 2592000],
      ['week', 604800],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
      ['second', 1],
    ];

    // Find the most appropriate time unit
    for (const [unit, secondsInUnit] of timeUnits) {
      const value = Math.floor(Math.abs(diffInSeconds) / secondsInUnit);
      if (value >= 1) {
        return rtf.format(diffInSeconds < 0 ? -value : value, unit as Intl.RelativeTimeFormatUnit);
      }
    }

    return rtf.format(0, 'second');
  } catch (error) {
    // Fallback formatting
    return formatFallbackRelativeTime(date);
  }
};

/**
 * Fallback relative time formatting for older browsers
 */
const formatFallbackRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Formats a file size in bytes to a human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Formats a number with proper thousands separators
 */
export const formatNumber = (
  num: number,
  locale: string = 'en-US',
  options?: Intl.NumberFormatOptions
): string => {
  try {
    return new Intl.NumberFormat(locale, options).format(num);
  } catch (error) {
    // Fallback for unsupported locales
    return num.toLocaleString();
  }
};

/**
 * Formats a percentage value
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1,
  locale: string = 'en-US'
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value / 100);
  } catch (error) {
    return `${value.toFixed(decimals)}%`;
  }
};

/**
 * Formats a date to a localized string
 */
export const formatDate = (
  date: Date,
  locale: string = 'en-US',
  options?: Intl.DateTimeFormatOptions
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  try {
    return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(date);
  } catch (error) {
    return date.toLocaleDateString();
  }
};

/**
 * Formats a time duration in milliseconds to human-readable format
 */
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Truncates text to a specified length with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
};

/**
 * Capitalizes the first letter of each word
 */
export const titleCase = (str: string): string => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
};

/**
 * Formats a phone number to a standard format
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Format based on length (assuming US/international format)
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }

  return phoneNumber; // Return original if can't format
};

/**
 * Formats a list of items into a readable sentence
 */
export const formatList = (
  items: string[],
  conjunction: string = 'and',
  locale: string = 'en-US'
): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;

  try {
    return new Intl.ListFormat(locale, { 
      style: 'long', 
      type: 'conjunction' 
    }).format(items);
  } catch (error) {
    // Fallback for older browsers
    const lastItem = items.pop();
    return `${items.join(', ')}, ${conjunction} ${lastItem}`;
  }
};

/**
 * Formats a color value to different formats
 */
export const formatColor = (color: string, format: 'hex' | 'rgb' | 'hsl' = 'hex'): string => {
  // This is a simplified version - in practice, you'd want a more robust color conversion library
  if (format === 'hex' && !color.startsWith('#')) {
    return `#${color}`;
  }
  
  return color;
};

/**
 * Formats a URL to be display-friendly
 */
export const formatDisplayUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.hostname}${urlObj.pathname !== '/' ? urlObj.pathname : ''}`;
  } catch (error) {
    return url;
  }
};

/**
 * Formats bytes per second to human-readable speed
 */
export const formatSpeed = (bytesPerSecond: number): string => {
  const speeds = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let i = 0;
  
  while (bytesPerSecond >= 1024 && i < speeds.length - 1) {
    bytesPerSecond /= 1024;
    i++;
  }
  
  return `${bytesPerSecond.toFixed(i === 0 ? 0 : 2)} ${speeds[i]}`;
};

/**
 * Formats a version number for display
 */
export const formatVersion = (version: string): string => {
  // Remove any leading 'v' and ensure proper semver format
  return version.replace(/^v/, '').trim();
};

/**
 * Formats an error message for user display
 */
export const formatErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as any).message);
  }
  
  return 'An unexpected error occurred';
};